import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, RequestHandlerScreen, TextArea } from 'common-src/components/base';
import { getCurrentUserId } from 'common-src/features/auth/selectors';
import {
  cachePortalThreadMessages,
  cachePortalThreads,
  getCachedPortalThreads,
} from 'common-src/features/cache';
import { apiRequest, getRestErrorMessage } from 'common-src/features/rest';
import { openDocument } from 'common-src/models/PatientDocument';
import { getPromptTemplatesRequest } from 'common-src/models/PromptTemplate';
import { getUniqueClientPromptTemplates } from 'common-src/models/PromptTemplate/selectors';
import { Colors, ColorsNew } from 'common-src/styles';

import Icons from 'src/assets/Icons';
import { ModalType } from 'src/components/base/ModalGroup';
import { ActionsRow, BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import BasePopup from 'src/popups/BasePopup';
import {
  getNormalizedThreadMessages,
  getNormalizedThreadsFromMessages,
  hasUnreadIncomingMessage,
  sendSecureMessage,
} from 'src/utils/secureMessagingUtils';

import styles from './SecureMessagingAll.module.scss';

const TableHeaders = [
  { column: 'subject', label: 'Subject', width: '35%', sortBy: 'subject' },
  { column: 'recipients', label: 'Recipients', width: '25%', sortBy: 'text' },
  { column: 'sentAt', label: 'Sent At', width: '20%', sortBy: 'value' },
  { column: 'actions', label: 'Actions', width: '20%' },
];

const SubjectCell = ({ subject, hasUnread }) => (
  <div className={styles.subjectCell}>
    {hasUnread && <span className={styles.unreadDot} />}
    <span className={[styles.subjectText, 'font-w-500', 'font-s-14'].join(' ')}>{subject}</span>
    {hasUnread && <span className={styles.newBadge}>New</span>}
  </div>
);

SubjectCell.propTypes = {
  subject: PropTypes.string,
  hasUnread: PropTypes.bool,
};

const getTableRows = (threads) =>
  threads.map((item) => {
    const subject = item?.subject?.props?.text ?? item?.subject ?? '-';
    const recipients = item?.recipients?.props?.text ?? item?.recipients ?? '-';
    const sentAt = item?.sentAt?.props?.text ?? item?.sentAt ?? '-';
    const sentAtValue =
      item?.sentAtValue ??
      item?.sentAt?.props?.value ??
      (sentAt !== '-' && moment(sentAt, 'MM/DD/YYYY', true).isValid()
        ? moment(sentAt, 'MM/DD/YYYY', true).valueOf()
        : -1);

    return {
      subject: <SubjectCell subject={subject} hasUnread={item.hasUnread} />,
      recipients: <TextItem isActive text={recipients} />,
      sentAt: <TextItem isActive text={sentAt} value={sentAtValue} />,
      actions: (
        <ActionsRow
          options={[
            {
              id: `view-conversation-button-${item.threadId}`,
              text: 'View Conversation',
              backgroundColor: '#555671B2',
              color: 'white',
              onClick: item.onView,
            },
          ]}
        />
      ),
      trClassNames: item.hasUnread ? [styles.unreadRow] : [],
    };
  });

const getMessageSenderLabel = (message) => {
  if (!message?.senderDisplayName) return '';

  const isClinic = `${message.senderType}`.toUpperCase() === 'CLINIC';
  if (isClinic && message.senderCredentials) {
    return `${message.senderDisplayName}, ${message.senderCredentials}`;
  }

  return message.senderDisplayName;
};

const SecureMessagingAll = ({ patientId }) => {
  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();
  const currentUserId = useCustomSelector((state) => getCurrentUserId(state));
  const cachedThreads = useCustomSelector((state) => getCachedPortalThreads(state, patientId));
  const promptTemplates = useCustomSelector((state) => getUniqueClientPromptTemplates(state));
  const [threads, setThreads] = useState(cachedThreads || []);
  const [isLoading, setIsLoading] = useState(!cachedThreads);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [selectedThread, setSelectedThread] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const replyAttachmentInputRef = useRef(null);
  const cachedThreadMessagesById = useCustomSelector(
    (state) => state.cache.portalThreadMessages || {},
  );
  const unreadThreadCount = threads.filter((thread) => thread.hasUnread).length;

  const resetReplyState = () => {
    setIsReplyOpen(false);
    setReplyText('');
    setReplyAttachment(null);
    if (replyAttachmentInputRef.current) {
      replyAttachmentInputRef.current.value = '';
    }
  };

  useEffect(() => {
    dispatch(getPromptTemplatesRequest());
  }, [dispatch]);

  useEffect(() => {
    if (cachedThreads) {
      setThreads(cachedThreads || []);
      setIsLoading(false);
    }
  }, [cachedThreads]);

  const closeConversation = () => {
    setSelectedThread(null);
    setConversationMessages([]);
    resetReplyState();
  };

  const fetchThreads = async () => {
    const response = await apiRequest({
      endpoint: `patientPortalMessages/patient/${patientId}`,
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(getRestErrorMessage(err) || 'Failed to fetch threads');
    }

    const data = await response.json();
    const payload =
      data?.PatientPortalMessage ??
      data?.patientPortalMessage ??
      data?.patient_portal_message ??
      [];
    const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const normalizedThreads = getNormalizedThreadsFromMessages(items, currentUserId);

    dispatch(cachePortalThreads(patientId, normalizedThreads));
    setThreads(normalizedThreads);
    return normalizedThreads;
  };

  const fetchThreadMessages = async (threadId) => {
    const response = await apiRequest({
      endpoint: `patientPortalMessages/thread/${threadId}`,
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(getRestErrorMessage(err) || 'Failed to fetch conversation');
    }

    const data = await response.json();
    const payload =
      data?.PatientPortalMessage ??
      data?.patientPortalMessage ??
      data?.patient_portal_message ??
      [];
    const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const normalizedMessages = getNormalizedThreadMessages(items);

    dispatch(cachePortalThreadMessages(threadId, normalizedMessages));
    setConversationMessages(normalizedMessages);
    return normalizedMessages;
  };

  const markThreadMessagesRead = async (threadId) => {
    const response = await apiRequest({
      endpoint: `patientPortalMessages/thread/${threadId}/read-all`,
      method: 'PUT',
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(getRestErrorMessage(err) || 'Failed to mark conversation as read');
    }

    const data = await response.json();
    const payload =
      data?.PatientPortalMessage ??
      data?.patientPortalMessage ??
      data?.patient_portal_message ??
      [];
    const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const normalizedMessages = getNormalizedThreadMessages(items);

    dispatch(cachePortalThreadMessages(threadId, normalizedMessages));
    setConversationMessages(normalizedMessages);

    const nextThreads = threads.map((thread) =>
      thread.threadId === threadId ? { ...thread, isRead: true, hasUnread: false } : thread,
    );
    dispatch(cachePortalThreads(patientId, nextThreads));
    setThreads(nextThreads);
    setSelectedThread((prevThread) =>
      prevThread?.threadId === threadId
        ? { ...prevThread, isRead: true, hasUnread: false }
        : prevThread,
    );

    return normalizedMessages;
  };

  useEffect(() => {
    const loadThreads = async () => {
      try {
        await fetchThreads();
      } catch (error) {
        showAlert(AlertType.Error, '', error.message || 'Failed to fetch threads');
      } finally {
        setIsLoading(false);
      }
    };

    loadThreads();
  }, [patientId, dispatch, currentUserId]);

  useEffect(() => {
    if (!selectedThread?.threadId) return;
    const cachedMessages = cachedThreadMessagesById[selectedThread.threadId];
    if (cachedMessages) {
      setConversationMessages(cachedMessages);
      setIsConversationLoading(false);
    }
  }, [selectedThread?.threadId, cachedThreadMessagesById]);

  const onOpenConversation = async (thread) => {
    setSelectedThread(thread);
    resetReplyState();
    const cachedMessages = cachedThreadMessagesById[thread.threadId];
    if (cachedMessages) {
      setConversationMessages(cachedMessages);
    }

    setIsConversationLoading(!cachedMessages);
    try {
      const messages = await fetchThreadMessages(thread.threadId);
      if (messages.some((message) => hasUnreadIncomingMessage(message, currentUserId))) {
        await markThreadMessagesRead(thread.threadId);
      }
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to fetch conversation');
    } finally {
      setIsConversationLoading(false);
    }
  };

  const onDownloadAttachment = async (attachmentId) => {
    if (!attachmentId) return;
    try {
      let fileName = `attachment-${attachmentId}`;
      const response = await apiRequest({ endpoint: `patientDocuments/${attachmentId}` });
      if (response.ok) {
        const data = await response.json();
        fileName =
          data?.PatientDocument?.fileName ??
          data?.patientDocument?.fileName ??
          data?.patient_document?.file_name ??
          fileName;
      }
      await openDocument(attachmentId, fileName, (error) => {
        showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
      });
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
    }
  };

  const onSendReply = async () => {
    if (!selectedThread?.threadId) {
      showAlert(AlertType.Error, '', 'Thread not found');
      return;
    }

    const threadMessage = conversationMessages[0];
    if (!threadMessage?.receiverType?.length || !threadMessage?.notificationType) {
      showAlert(AlertType.Error, '', 'Missing thread metadata needed to send reply');
      return;
    }

    try {
      const currentThreadId = selectedThread.threadId;

      await sendSecureMessage({
        patientId,
        messageSubject: selectedThread?.subject,
        body: replyText.trim(),
        sendToPatient: !!selectedThread?.sendToPatient,
        sendToCaregiver: !!selectedThread?.sendToCaregiver,
        receiverType: threadMessage.receiverType,
        notificationType: threadMessage.notificationType,
        attachment: replyAttachment,
        threadId: currentThreadId,
      });

      setIsConversationLoading(true);
      await fetchThreadMessages(currentThreadId);
      const refreshedThreads = await fetchThreads();
      const refreshedThread =
        refreshedThreads.find((thread) => thread.threadId === currentThreadId) || selectedThread;
      setSelectedThread(refreshedThread);
      resetReplyState();
      showAlert(AlertType.Success, '', 'Message sent successfully!');
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to send message');
    } finally {
      setIsConversationLoading(false);
    }
  };

  const onReplyAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    setReplyAttachment(file);
  };

  if (isLoading) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling threads..." />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.titleRow}>
          <h3 className={[styles.title, 'font-s-24', 'font-w-700'].join(' ')}>All Messages</h3>
          {!!unreadThreadCount && (
            <span className={styles.headerBadge}>{unreadThreadCount} new</span>
          )}
        </div>
        <Button
          id="send-secure-message-button"
          text="Send Message"
          classNames={[styles.sendMessageButton]}
          onClick={() =>
            dispatch(
              openModal(ModalType.SEND_SECURE_MESSAGE, {
                patientId,
              }),
            )
          }
        />
      </div>
      <BaseTable
        headers={TableHeaders}
        data={getTableRows(
          threads.map((thread) => ({
            ...thread,
            onView: () => onOpenConversation(thread),
          })),
        )}
        name="secure-messages"
        classNames={[styles.table, threads?.length <= ResultsCount[25] ? styles.noPagination : '']}
        emptyText="No threads"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'sentAt',
          label: 'Sent At',
          width: '10%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination
        onChangePageHandler={setPaginationData}
        results={threads}
        selectedTab="secure-messaging"
        containerClassNames={[styles.pagination]}
      />
      {!!selectedThread && (
        <BasePopup
          id="secure-thread-conversation"
          open
          onClose={closeConversation}
          title={selectedThread.subject || 'Conversation'}
        >
          <div className={styles.messagePopupContent}>
            <div className={styles.messagePopupBody}>
              {isConversationLoading && <div>Loading conversation...</div>}
              {!isConversationLoading && !conversationMessages.length && <div>No messages</div>}
              {!isConversationLoading &&
                conversationMessages.map((message) => {
                  const isClinic = `${message.senderType}`.toUpperCase() === 'CLINIC';
                  const senderLabel = getMessageSenderLabel(message);
                  return (
                    <div
                      key={message.id || `${message.sentAt}-${message.body}`}
                      className={[
                        styles.bubbleRow,
                        isClinic ? styles.bubbleRowRight : styles.bubbleRowLeft,
                      ].join(' ')}
                    >
                      <div
                        className={[
                          styles.bubbleContainer,
                          isClinic ? styles.bubbleContainerRight : styles.bubbleContainerLeft,
                        ].join(' ')}
                      >
                        {!!senderLabel && (
                          <div
                            className={[
                              styles.senderLabel,
                              isClinic ? styles.senderLabelRight : styles.senderLabelLeft,
                            ].join(' ')}
                          >
                            {senderLabel}
                          </div>
                        )}
                        <div
                          className={[
                            styles.bubble,
                            isClinic ? styles.bubbleClinic : styles.bubbleOther,
                          ].join(' ')}
                        >
                          <div>{message.body}</div>
                          {!!message.attachmentId && (
                            <button
                              type="button"
                              className={styles.attachmentButton}
                              onClick={() => onDownloadAttachment(message.attachmentId)}
                            >
                              Download attachment
                            </button>
                          )}
                          <div className={styles.bubbleMeta}>{message.sentAt}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className={styles.replySection}>
              {!isReplyOpen && (
                <div className={styles.replyButtonRow}>
                  <Button
                    id="secure-thread-close-button"
                    text="Close"
                    classNames={[styles.replyButton]}
                    textColor={ColorsNew.darkGreen}
                    backgroundColor={Colors.white}
                    borderColor={ColorsNew.darkGreen}
                    onClick={closeConversation}
                  />
                  <Button
                    id="secure-thread-reply-button"
                    text="Reply"
                    classNames={[styles.replyButton]}
                    onClick={() => setIsReplyOpen(true)}
                  />
                </div>
              )}
              {isReplyOpen && (
                <div className={styles.replyComposer}>
                  <TextArea
                    id="secure-thread-reply-input"
                    type="template"
                    placeholder="Enter message"
                    value={replyText}
                    onTextChange={setReplyText}
                    templates={promptTemplates}
                    fixedRows={4}
                  />
                  <div className={styles.replyFooter}>
                    <div className={styles.replyAttachSection}>
                      <input
                        ref={replyAttachmentInputRef}
                        type="file"
                        className={styles.replyAttachmentInput}
                        onChange={onReplyAttachmentChange}
                      />
                      <Button
                        id="secure-thread-reply-attach-button"
                        text="Attach"
                        classNames={[styles.replyAttachButton]}
                        textColor={ColorsNew.darkGreen}
                        backgroundColor={Colors.white}
                        borderColor={Colors.mediumDarkGray}
                        onClick={() => replyAttachmentInputRef.current?.click()}
                      />
                      {!!replyAttachment && (
                        <span className={styles.replyAttachmentName}>{replyAttachment.name}</span>
                      )}
                    </div>
                    <div className={styles.replyActions}>
                      <Button
                        id="secure-thread-reply-cancel-button"
                        text="Cancel"
                        classNames={[styles.replyActionButton]}
                        textColor={ColorsNew.darkGreen}
                        backgroundColor={Colors.white}
                        borderColor={ColorsNew.darkGreen}
                        onClick={resetReplyState}
                      />
                      <Button
                        id="secure-thread-reply-send-button"
                        text="Send Reply"
                        classNames={[styles.replyActionButton]}
                        disabled={!replyText.trim()}
                        rightIconSrc={Icons.sendIcon}
                        onClick={onSendReply}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </BasePopup>
      )}
    </div>
  );
};

SecureMessagingAll.propTypes = {
  patientId: PropTypes.number.isRequired,
};

export default SecureMessagingAll;
